import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Store } from '@ngrx/store';
import { API_URLS } from '@core/api-url.enum';
import { ApiCallService } from '@services/api-call.service';
import { AuthService } from '@core/auth.service';
import { Application } from '@enums/application.enums';
import { CommonService } from '@modules/shared/services/common.service';
import * as fromUserProfile from '@modules/shared/user-profile.reducer';
import * as userProfileAction from '@modules/shared/user-profile.action';

const USER_NOTIFICATION_CONSTANTS = {
  QUERY: {
    WHITE_LABEL: 'is_white_label=true',
    STANDARD: 'is_white_label=false',
    HOST_NAME: 'hostName',
    NULL_HOST: 'null'
  }
} as const;

@Injectable({
  providedIn: 'root'
})
export class UserNotificationService implements OnDestroy {
  private readonly isBrowser: boolean;
  private pollIntervalId: ReturnType<typeof setInterval> | null = null;
  private isTabActive = true;
  private visibilityBound = false;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private readonly apiCallService: ApiCallService,
    private readonly authService: AuthService,
    private readonly cs: CommonService,
    private readonly store: Store<fromUserProfile.UserProfileState>,
    private readonly ngZone: NgZone
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  start(): void {
    if (!this.isBrowser || !this.authService.isAuthenticated()) {
      return;
    }

    this.bindVisibilityListeners();
    if (this.pollIntervalId) {
      return;
    }

    this.refresh();
    this.startPolling();
  }

  refresh(): void {
    if (!this.isBrowser || !this.authService.isAuthenticated()) {
      return;
    }

    const config = this.cs.getWhiteLabelConfiguration();
    const url = config
      ? `${API_URLS.Notification}?${USER_NOTIFICATION_CONSTANTS.QUERY.WHITE_LABEL}&${USER_NOTIFICATION_CONSTANTS.QUERY.HOST_NAME}=${this.cs.getHostUrl}`
      : `${API_URLS.Notification}?${USER_NOTIFICATION_CONSTANTS.QUERY.STANDARD}&${USER_NOTIFICATION_CONSTANTS.QUERY.HOST_NAME}=${USER_NOTIFICATION_CONSTANTS.QUERY.NULL_HOST}`;

    this.apiCallService.get(url, false).subscribe({
      next: (res) => {
        if (res.status) {
          this.store.dispatch(new userProfileAction.SetRefreshKey(res.data));
        }
      },
      error: () => {}
    });
  }

  stop(): void {
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private startPolling(): void {
    if (this.pollIntervalId) {
      return;
    }

    // A recurring timer inside Angular's zone prevents post-hydration cleanup.
    this.ngZone.runOutsideAngular(() => {
      this.pollIntervalId = setInterval(() => {
        if (!this.authService.isAuthenticated()) {
          this.stop();
          return;
        }

        if (this.isBrowser && window?.navigator?.onLine && this.isTabActive) {
          this.ngZone.run(() => this.refresh());
        }
      }, Application.ONE_THOUSAND * (Application.SIXTY * Application.TWO));
    });
  }

  private bindVisibilityListeners(): void {
    if (!this.isBrowser || this.visibilityBound) {
      return;
    }

    this.visibilityBound = true;
    window.addEventListener('blur', this.onTabBlur);
    window.addEventListener('focus', this.onTabFocus);
  }

  private readonly onTabBlur = (): void => {
    this.isTabActive = false;
  };

  private readonly onTabFocus = (): void => {
    this.isTabActive = true;
  };
}
